import React from "react";
import PropTypes from "prop-types";
import { withRouter } from "tools/routerCompat";
import { Link } from "react-router-dom";
import Button from "@mui/material/Button";
import Grid from "@mui/material/Grid";
import * as yup from "yup";
import { MSpinner, MSplitter, MDialog} from "components/Shared";
import MGrid, {
  COL_CODE_FULL_NAME,
  COL_CODE_EMAIL,
  ACTION_ADD,
} from "components/Shared/MGrid.tsx";
import MCard from "components/Shared/MCard.tsx";
import {
  DATA_TYPE_PHONE_NUMBER,
  DATA_TYPE_EMAIL,
  DATA_TYPE_MULTI_LINE_STRING,
  DATA_TYPE_DROPDOWN2,
} from "typeConstants/dataTypes";
import { underwriterSearchByMajorCompanyCode } from "requests/underwriter";
import { InternetUser, CreateUser } from "containers/User";

const notificationMethods = [
  { label: "NONE", value: "NONE" },
  { label: "EMAIL", value: "EMAIL" },
  { label: "FAX", value: "FAX" },
  { label: "MAIL", value: "MAIL" }
];

class CustomerAgentProfile extends React.Component {
  constructor(props) {
    super(props);
    this.state = {
      isLoading: true,
      underwriters: [],
      displayAddUserDialog: false
    };
    this.getFields = this.getFields.bind(this);
    this.saveFields = this.saveFields.bind(this);
    this.getAgentId = this.getAgentId.bind(this);
    this.loadUnderwriters = this.loadUnderwriters.bind(this);
    this.getUnderwriterDDValue = this.getUnderwriterDDValue.bind(this);
    this.submitClicked = this.submitClicked.bind(this);
    this.notificationFieldsFilledOut = this.notificationFieldsFilledOut.bind(this);
    this.getUsersMGrid = this.getUsersMGrid.bind(this);
    this.isFetching = this.isFetching.bind(this);
    this.onUserListClicked = this.onUserListClicked.bind(this);
    this.showAddUserDialog = this.showAddUserDialog.bind(this);
    this.hideAddUserDialog = this.hideAddUserDialog.bind(this);

    this.mounted = { current: true };
    this.cardControlMethods = null;
    this.state = { selectedUserId: null, displayAddUserDialog: false};
  }

  componentDidMount() {
    if (!this.getAgentId() && !this.props.isCreating) this.props.history.push({ pathname: "/" });
    if (!this.props.majorCompanies || !this.props.majorCompanies.length) {
      this.props.getMajorCompanies();
    }

    if (!this.props.isCreating) this.props.agentGet(this.getAgentId());
  }

  componentDidUpdate(prevProps) {
    if (prevProps.agentId !== this.props.agentId && !this.props.isCreating) {
      this.props.agentGet(this.getAgentId());
      this.setState({
        selectedUserId: null,
        displayAddUserDialog: false}
      );
    }

    let justLoadedAgentDetais = !prevProps.agentDetails && this.props.agentDetails;
    let loadedAgentCompanyChanged = prevProps.agentDetails && this.props.agentDetails && prevProps.agentDetails.majorCompanyCode != this.props.agentDetails.majorCompanyCode;

    if ((justLoadedAgentDetais || loadedAgentCompanyChanged) && this.props.agentDetails.majorCompanyCode) {
      this.loadUnderwriters(this.props.agentDetails.majorCompanyCode);
    }

    if (this.props.isCreating && prevProps.isSavingAgent && !this.props.isSavingAgent) {
      this.props.makeToast("Agent Created", "success");

      this.props.history.push({
        pathname: "/customer-agent/profile",
        state: {
          agentId: this.props.agentDetails.id,
          displayAddUserDialog: false
        }
      });
    }

    return true;
  }

  componentWillUnmount() {
    this.mounted.current = false;
  }

  loadUnderwriters(majorCompanyCode) {
    if (majorCompanyCode)
      underwriterSearchByMajorCompanyCode(majorCompanyCode, u => this.setState({ underwriters: u }), this.mounted);
  }

  onUserListClicked(index) {
    this.setState({
      selectedUserId: this.props.agentDetails.users[index].id,
      displayAddUserDialog: false}
    );
  }

  getAgentId() {
    return this.props.agentId || (this.props.location && this.props.location.state ? this.props.location.state.agentId : null);
  }

  notificationFieldsFilledOut(updatedAgent) {
    if (updatedAgent.notificationMethod === "EMAIL" && !updatedAgent.emailAddress) {
      this.props.makeToast("Agent email is required if notification method is EMAIL", "error");
      return false;
    }

    if (updatedAgent.notificationMethod === "FAX" && !updatedAgent.faxNumber) {
      this.props.makeToast("Agent fax is required if notification method is FAX", "error");
      return false;
    }

    return true;
  }

  saveFields(originalValueMappings, updatedValues) {
    let updatedAgent = {
      ...this.props.agentDetails,
      ...updatedValues
    };

    if (this.notificationFieldsFilledOut(updatedAgent)) {
      this.props.agentUpdate(updatedAgent);
      return true;
    } else {
      return false;
    }

  }

  submitClicked() {
    if (this.cardControlMethods.runValidation(true)) {
      let updatedAgent = this.cardControlMethods.getEditedDataObject();
      if (this.notificationFieldsFilledOut(updatedAgent)) {
        this.props.agentCreate(updatedAgent);
      }
    }
  }

  getUnderwriterDDValue(uwCode) {
    if (!uwCode) return null;
    if (this.state.underwriters) {
      let uw = this.state.underwriters.find(u => u.underwriterCode === uwCode);
      if (uw) return {
        label: uw.displayName,
        value: uw.underwriterCode
      };
      else return { label: uwCode };
    } else {
      return { label: "Underwriter list loading" };
    }
  }

  getFields() {
    let { agentDetails, isCreating } = this.props;
    let fields = [];

    //making agentDetails an empty object is the easiest way to avoid null ref errors below in create mode
    if (isCreating) agentDetails = {};

    let majorCompanyDropdownValues = this.props.majorCompanies.map(v => ({
      label: v.companyName,
      value: v.code,
    }));

    fields = [{
      key: "majorCompanyCode",
      value: { label: agentDetails.majorCompanyName, value: agentDetails.majorCompanyCode },
      label: "Major Company",
      editable: true,
      dataType: DATA_TYPE_DROPDOWN2,
      // list of major companies here
      dataTypeOptions: {
        options: majorCompanyDropdownValues
      },
      onChange: option => {
        this.loadUnderwriters(option.value);
        this.cardControlMethods.setEditData("underwriterCode", null);
      }
    },
    {
      key: "agentCode",
      value: agentDetails.agentCode,
      label: "Agent Code",
      editable: isCreating,
      validation: yup.string().nullable().required("Agent Code is required")
    },
    {
      key: "masterCode",
      value: agentDetails.masterCode,
      label: "Master Code"
    },
    {
      key: "underwriterCode",
      value: this.getUnderwriterDDValue(agentDetails.underwriterCode),
      editable: !!(this.state.underwriters && this.state.underwriters.length),
      label: "Underwriter Name",
      dataType: DATA_TYPE_DROPDOWN2,
      dataTypeOptions: {
        options: this.state.underwriters && this.state.underwriters.map(v => ({
          label: v.displayName,
          value: v.underwriterCode
        }))
      },
    },
    {
      key: "agentName",
      value: agentDetails.agentName,
      label: "Name",
      validation: yup.string().nullable().required("Agent Name is required")
    },
    {
      key: "addressLine1",
      value: agentDetails.addressLine1,
      label: "Address 1"
    },
    {
      key: "addressLine2",
      value: agentDetails.addressLine2,
      label: "Address 2"
    },
    {
      key: "addressCity",
      value: agentDetails.addressCity,
      label: "City"
    },
    {
      key: "addressStateAbbreviation",
      value: agentDetails.addressStateAbbreviation,
      label: "State"
    },
    {
      key: "addressPostalCode",
      value: agentDetails.addressPostalCode,
      label: "Zip Code"
    },
    {
      key: "phoneNumber",
      value: agentDetails.phoneNumber,
      label: "Phone Number",
      dataType: DATA_TYPE_PHONE_NUMBER
    },
    {
      key: "faxNumber",
      value: agentDetails.faxNumber,
      label: "Fax Number",
      dataType: DATA_TYPE_PHONE_NUMBER,
    },
    {
      key: "emailAddress",
      value: agentDetails.emailAddress,
      label: "Email Address",
      dataType: DATA_TYPE_EMAIL,
      validation: yup.string().nullable().email("Must be a valid email address")
    },
    {
      key: "notificationMethod",
      value: notificationMethods.find(m => m.value == agentDetails.notificationMethod),
      label: "Notification Method",
      dataType: DATA_TYPE_DROPDOWN2,
      dataTypeOptions: {
        options: notificationMethods
      }
    },
    {
      key: "comments",
      value: agentDetails.comments,
      label: "Comments",
      dataType: DATA_TYPE_MULTI_LINE_STRING
    }
    ];

    return fields;
  }

  getCreateButtons() {
    if (this.props.isSavingAgent) {
      return (<>
        <Grid size={6} />
        <Grid size={6}>
          <MSpinner />
        </Grid></>);
    }

    return (<>
      <Grid size={6} />
      <Grid size={6}>
        <Link to="/customer-agents" className="link">
          <Button
            style={{ float: "right", marginRight: 20 }} color="secondary"
            className="m-t-10 save"
          >Cancel</Button>
        </Link>
        <Button
          style={{ float: "right", marginRight: 20 }}
          type="submit"
          className="m-t-10 save"
          onClick={this.submitClicked}
        >Create</Button>
      </Grid>
    </>);
  }

  hideAddUserDialog(){
    this.setState({displayAddUserDialog: false});
  }

  showAddUserDialog() {
    this.setState({displayAddUserDialog: true});
  }

  getUsersMGrid() {
    const fields = [
      {
        key: "username",
        name: "User Name",
        width: 300,
        dataSource: "username"
      },
      { code: COL_CODE_EMAIL, dataSource: "email" },
      { code: COL_CODE_FULL_NAME, dataSource: "fullName" },
    ];

    let { agentDetails } = this.props;
    return (<MGrid
      columnDefinitions={fields}
      data={agentDetails && agentDetails.users}
      isFetchingData={this.isFetching()}
      actions={[{ code: ACTION_ADD, onClick: () => this.showAddUserDialog() }]}
      onRowClick={this.onUserListClicked} />);
  }

  isFetching() {
    const { agentDetails, isCreating, majorCompanies } = this.props;
    const hasAgentInfo = !agentDetails || Object.keys(agentDetails).length === 0;
    return (hasAgentInfo && !isCreating) || !majorCompanies;
  }

  render() {
    const { agentDetails, isCreating, verticalDisplay, canEdit } = this.props;
    const cellWidth = (verticalDisplay || this.state.selectedUserId) ? 12 : 6;
    const isFetching = this.isFetching();

    try {
      return (
          <>
        <MSplitter
          firstChild={
            <Grid container spacing={1}>
              <Grid size={cellWidth}>
                <MCard
                  isFetching={isFetching}
                  title="Customer Agent Maintenance"
                  fields={isFetching && !isCreating ? null : this.getFields()}
                  onEditFinished={!isCreating && this.saveFields}
                  isEditable={!isFetching && canEdit}
                  getControlMethods={cm => this.cardControlMethods = cm}
                  onlyEditable={isCreating}
                />
              </Grid>
              {this.props.isCreating && this.getCreateButtons()}
              {!this.props.isCreating && agentDetails &&
                <Grid size={cellWidth} sx={{ minWidth: 0, overflow: "hidden" }}>
                  <MCard
                    title="Users"
                    fields={[{ key: "mgrid", control: this.getUsersMGrid() }]}
                    disableHoverStyles
                    sx={{ '& .mgrid-react-data-grid': { height: 500 } }}
                  />
                </Grid>}
            </Grid>}
          secondChild={this.state.selectedUserId && <InternetUser internetUserOverride={this.state.selectedUserId} verticalDisplay />}
          secondChildTitle={{
            title: "User Login",
            buttons: [
              { icon: "close", onClick: () => this.setState({ selectedUserId: null }) }
            ]
          }}
          splitHorizontal={verticalDisplay}
        />
          <MDialog
            visible={this.state.displayAddUserDialog}
            width={600}
            height={"flex"}
            focusOnMount={false}
          >
            <CreateUser
                onCancelClicked={this.hideAddUserDialog}
                rolesList={["Agent"]}
            />

          </MDialog>

          </>
      );
    } catch (e) {
      return (<div>{e.toString()}</div>);
    }
  }
}

CustomerAgentProfile.propTypes = {
  location: PropTypes.object,
  history: PropTypes.object,
  agentGet: PropTypes.func,
  agentUpdate: PropTypes.func,
  agentCreate: PropTypes.func,
  getMajorCompanies: PropTypes.func.isRequired,
  majorCompanies: PropTypes.array,
  agentId: PropTypes.number,
  agentDetails: PropTypes.object,
  verticalDisplay: PropTypes.bool,
  makeToast: PropTypes.func,
  isCreating: PropTypes.bool,
  isSavingAgent: PropTypes.bool,
  canEdit: PropTypes.bool,
};

CustomerAgentProfile.defaultProps = {
  isCreating: false
};

export default withRouter(CustomerAgentProfile);
